"use client";

import dynamic from "next/dynamic";
import { IconType } from "react-icons";

import useCountries from "@/app/hooks/useCountries";
import { SafeListing } from "@/app/types";

import ListingCategory from "./ListingCategory";
import { useRouter } from "next/navigation";
import MapWithCoordinates from "../MapWithCoordinates";
import { GrLocation } from "react-icons/gr";
import { IoMdTime } from "react-icons/io";
import { CgSandClock } from "react-icons/cg";
import { MdPeopleOutline } from "react-icons/md";
import { LiaLanguageSolid } from "react-icons/lia";
import { FaRegCheckCircle, FaRegStar, FaRegTimesCircle, FaStar } from "react-icons/fa";
import { GiScubaTanks } from "react-icons/gi";
import Rating from "../Rating";
import { TbSpeedboat } from "react-icons/tb";
import { formatListingBoat, formatNumberOfDives, parseListingItems } from "@/app/utils/listingFormat";
import { getDiveCenterUrl } from "@/app/utils/slugify";
import { resolveGoogleBusinessProfileName } from "@/lib/googleBusinessProfiles";

const Map = dynamic(() => import("../Map"), {
  ssr: false,
});

interface ListingInfoProps {
  diveCenterId: string;
  userName: string | null;
  description: string;
  category:
  | {
    icon: IconType;
    label: string;
    description: string;
  }
  | undefined;
  locationValue: string;
  listing: SafeListing;
}

const ListingInfo: React.FC<ListingInfoProps> = ({
  userName,
  description,
  category,
  locationValue,
  listing,
}) => {
  const router = useRouter();
  const { getByValue } = useCountries();

  const coordinates = getByValue(locationValue)?.latlng;

  const handleGetDirections = () => {
    const latitude = listing.diveCenter?.latitude || 0;
    const longitude = listing.diveCenter?.longitude || 0;

    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;

    window.open(directionsUrl, "_blank");
  };

  const centerData = {
    name: listing.diveCenter.user.name,
    agency: listing.diveCenter.agency,
    // currency: listing.user.currency,
    city: listing.diveCenter.city,
    country: listing.diveCenter.country,
  };

  const urlWithParamsCenter = `${getDiveCenterUrl(listing.diveCenter.user.name, listing.diveCenterId)}?centerData=${encodeURIComponent(
    JSON.stringify(centerData)
  )}`;

  // Google Things to do requires the operator's Google Business Profile name
  // to be visible on the landing page when it differs from our display name.
  const gbpOperatorName = resolveGoogleBusinessProfileName(
    listing.diveCenterId,
    listing.diveCenter.user.name
  );

  const buRating = listing.diveCenter.averageRating ?? 0;
  const useBuReviews = buRating > 0;
  const taId = listing.diveCenter.tripAdvisorLocationId;
  const taUrl = taId
    ? (taId.includes('-')
        ? `https://www.tripadvisor.com/Attraction_Review-${taId}-Reviews.html`
        : `https://www.tripadvisor.com/Attraction_Review-d${taId}-Reviews.html`)
    : undefined;
  // Without a TripAdvisor location id there is no page where the visitor can
  // verify the TripAdvisor reviews, so the rating is not shown at all (Google
  // QA requires any displayed rating count to be verifiable).
  const effectiveRating = useBuReviews
    ? buRating
    : (taId ? (listing.diveCenter.averageRatingTripAdvisor ?? 0) : 0);
  const effectiveReviews = useBuReviews
    ? (listing.diveCenter.totalReviews ?? 0)
    : (taId ? (listing.diveCenter.totalReviewsTripAdvisor ?? 0) : 0);

  return (
    <div className="col-span-4 flex flex-col gap-2">
      <div className="flex flex-col gap-2">
        <div
          className="
            text-xl 
            font-semibold 
            flex 
            flex-row 
            items-center
            gap-2
          "
        >
          <div>
            Hosted by{" "}
            {/* <span
              className="cursor-pointer hover:underline"
              onClick={() => router.push(`/dive-center/${diveCenterId}`)}
            >
              {user?.name}
            </span> */}
            <span className="cursor-pointer hover:underline capitalize">
              <a
                href={`${urlWithParamsCenter}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {userName}
              </a>
            </span>
          </div>
          {/* <Avatar src={user?.image} /> */}
        </div>
        <div className="mb-3">
          <Rating
            averageRating={effectiveRating}
            reviewsNumber={effectiveReviews}
            reviewsSource={useBuReviews ? 'internal' : 'tripadvisor'}
            reviewsLinkHref={
              !useBuReviews && taUrl ? taUrl : undefined
            }
          />
        </div>
      </div>
      <hr />
      {category && (
        <ListingCategory
          icon={category.icon}
          label={category?.label}
          description={category?.description}
        />
      )}
      <hr />
      <div className="text-md text-justify font-light text-neutral-500">
        {description}
      </div>
      <hr />
      <div className="font-light text-neutral-500 gap-1 p-2">
        <p className="font-bold text-lg text-black pb-2">KEY INFO</p>
        <div className="font-light text-neutral-500 gap-1 flex">
          <div className="flex flex-col items-center mr-4 ">
            <div className="font-light text-neutral-600 text-justify py-2">
              <IoMdTime size={21} />
            </div>
            <div className="font-light text-neutral-600 text-justify py-2">
              <CgSandClock size={21} />
            </div>
            <div className="font-light text-neutral-600 text-justify py-2">
              <GiScubaTanks size={21} />
            </div>
            <div className="font-light text-neutral-600 text-justify py-2">
              <MdPeopleOutline size={21} />
            </div>
            <div className="font-light text-neutral-600 text-justify py-2">
              <LiaLanguageSolid size={21} />
            </div>
            <div className="font-light text-neutral-600 text-justify py-2">
              <TbSpeedboat size={21} />
            </div>
          </div>
          <div className="flex flex-col items-cenrt text-black font-medium">
            <div className="text-justify pt-2 pb-1">
              {listing?.startTime || "To be determined"}
            </div>
            <div className="text-justify pt-2 pb-1">
              {listing?.length || "-"}   {listing?.lengthUnit || "-"} (approx.)
            </div>
            <div className="text-justify pt-2 pb-1">
              {formatNumberOfDives(listing?.numberOfDives, listing?.category)}
            </div>
            <div className="text-justify pt-2 pb-1">
              Max {listing?.students || "-"} students / instructor
            </div>
            <div className="text-justify pt-2 pb-1">
              {listing.diveCenter?.languages ? listing.diveCenter.languages.replace(/^\s*,+\s*|\s*,+\s*$/g, '') : "-"}
            </div>
            <div className="text-justify pt-2 pb-1 capitalize">
              {formatListingBoat(listing?.boat, listing?.numberOfDives, listing?.category)}
            </div>
          </div>
        </div>
      </div>
      <hr />
      {/* <Map center={coordinates} /> */}
      <div className="font-light text-neutral-500 gap-1 p-2">
        <p className="font-bold text-lg text-black pb-4">
          MEET AT THE DIVE CENTER
        </p>
        <div className="flex gap-2">
          <GrLocation size={24} className="text-neutral-700" />
          <div className="flex flex-col">
            <p className="text-md">
              {listing.diveCenter?.address || ""},{" "}
              {listing.diveCenter?.city || ""},{" "}
              {listing.diveCenter?.postalCode || ""},{" "}
              {listing.diveCenter?.country || ""}
            </p>
            <p
              onClick={handleGetDirections}
              className="font-bold text-sm text-cyan-700 cursor-pointer hover:underline mt-2"
            >
              Get directions
            </p>
          </div>
        </div>
      </div>
      <MapWithCoordinates
        latitude={listing.diveCenter?.latitude || 0}
        longitude={listing.diveCenter?.longitude || 0}
      />
      <hr />
      <div className="font-light text-neutral-500 p-2">
        <p className="font-bold text-lg text-black pb-2">WHAT'S INCLUDED</p>
        <ul className="flex flex-col font-light text-neutral-600 text-justify">
          {parseListingItems(listing?.included, { category: listing?.category }).length > 0 ? (
            parseListingItems(listing?.included, { category: listing?.category })
              .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
              .map((item, index) => (
                <li key={index}>
                  <div className="flex items-center mb-1">
                    <FaRegCheckCircle size={18} className="mr-2 text-green-700" />
                    {item.charAt(0).toUpperCase() + item.slice(1)}
                  </div>
                </li>
              ))
          ) : (
            <li className="text-neutral-400 italic">No items</li>
          )}
        </ul>
      </div>
      <div className="font-light text-neutral-500 gap-1 p-2">
        <p className="font-bold text-lg text-black pb-2">NOT INCLUDED</p>
        <ul className="flex flex-col font-light text-neutral-600 text-justify">
          {parseListingItems(listing?.notIncluded).length > 0 ? (
            parseListingItems(listing?.notIncluded)
              .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
              .map((item, index) => (
                <li key={index}>
                  <div className="flex items-center mb-1">
                    <FaRegTimesCircle size={18} className="mr-2 text-red-700" />
                    {item.charAt(0).toUpperCase() + item.slice(1)}
                  </div>
                </li>
              ))
          ) : (
            <li className="text-neutral-400 italic">No items</li>
          )}
        </ul>
      </div>
      <div className="font-light text-neutral-500 gap-1 p-2">
        <p className="font-bold text-lg text-black pb-2">REQUIREMENTS</p>
        {listing?.minAge === "" && listing?.canSwim === ""  && listing?.minCertification === "" && listing?.minLoggedDives === "" ? (
          <div className="flex text-black font-medium">No requirements</div>
        ) : (
          <div className="font-light text-neutral-600 text-justify gap-1 flex">
            <div className="flex flex-col mr-4 ">
              <div className="font-light text-neutral-600 text-justify py-1">
                Minimum Age
              </div>
              <div className="font-light text-neutral-600 text-justify py-1">
                Be able to swim
              </div>
               <div className="font-light text-neutral-600 text-justify py-1">
                Min. certification
              </div>
              <div className="font-light text-neutral-600 text-justify py-1">
                Min logged dives
              </div>
            </div>
            <div className="flex flex-col text-black font-medium">
              <div className="text-justify py-1">
                {listing?.minAge || "-"}
              </div>
              <div className="text-justify py-1">
                {listing?.canSwim || "-"}
              </div>
              <div className="text-justify py-1">
                {listing?.minCertification || "None"}
              </div>
              <div className="text-justify py-1">
                {listing?.minLoggedDives || "None"}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-1 p-2">
        <button className="bg-cyan-700 hover:bg-cyan-600 text-white font-bold px-5 py-3 rounded-lg">
          <a href="#book-now">BOOK NOW</a>
        </button>
      </div>
    </div>
  );
};

export default ListingInfo;
